import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import CommonService from "../../services/common.service";
import { buildInspectionReportLocation } from "../../utils/qaInspectionFlow";
import { API_URLS } from "../../constants/api-urls";
import "./QAAssignmentAccept.scss";

const QA_FEE_PERCENT = 0.25;

function parseQAFeeBreakdown(additionalNotes) {
  if (!additionalNotes || typeof additionalNotes !== "string") return null;
  const line = additionalNotes.split("\n")[0] || "";
  if (!line.includes("Professional fee:") || !line.includes("Subtotal:")) return null;
  const result = { baseInspectionFee: 0, travelLogistics: 0, regionalTaxes: 0, platformEscrowFee: 0, subtotal: 0, totalWithFee: 0 };
  const patterns = [
    { key: "baseInspectionFee", regex: /Professional fee:\s*([\d.]+)/i },
    { key: "travelLogistics", regex: /Travel\s*[&]\s*logistics:\s*([\d.]+)/i },
    { key: "regionalTaxes", regex: /Taxes:\s*([\d.]+)/i },
    { key: "subtotal", regex: /Subtotal:\s*([\d.]+)/i },
    { key: "totalWithFee", regex: /Total\s*\(incl[^)]*\):\s*([\d.]+)/i },
  ];
  patterns.forEach(({ key, regex }) => {
    const m = line.match(regex);
    if (m) result[key] = parseFloat(m[1]) || 0;
  });
  result.platformEscrowFee = Math.round(result.subtotal * QA_FEE_PERCENT * 100) / 100;
  return result;
}

export default function QAAssignmentAccept() {
  const { inspectionId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [declining, setDeclining] = useState(false);
  const [inspection, setInspection] = useState(null);
  const [qaRequest, setQaRequest] = useState(null);
  const [p2pAssignment, setP2pAssignment] = useState(null);
  const [siteContact, setSiteContact] = useState({ name: "", phone: "" });
  const [financial, setFinancial] = useState({
    buyerTotalPaid: 0,
    platformCommission: 0,
    youReceive: 0,
    currency: "USD",
  });
  const [inspectionTypes, setInspectionTypes] = useState([]);
  const [starting, setStarting] = useState(false);

  const loadInspection = useCallback(async () => {
    if (!inspectionId) return null;
    const inspectionRes = await CommonService.get(API_URLS.QA.INSPECTION_BY_ID(inspectionId));
    const insp = inspectionRes?.data ?? null;
    if (!insp) return null;
    setInspection(insp);
    setP2pAssignment(insp.p2p_assignment ?? null);
    return insp;
  }, [inspectionId]);

  useEffect(() => {
    if (!inspectionId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    async function fetchData() {
      try {
        const insp = await loadInspection();
        if (cancelled || !insp) {
          setLoading(false);
          return;
        }
        const qaRequestRes = await CommonService.get(API_URLS.QA.GET_BY_ID(insp.qa_request_id));
        const qr = qaRequestRes?.data ?? null;
        if (!cancelled) setQaRequest(qr);

        let contact = { name: "", phone: "" };
        if (qr?.requested_by_user_id) {
          try {
            const companyRes = await CommonService.get(`/company/profile/${qr.requested_by_user_id}`);
            const co = companyRes?.data ?? companyRes ?? {};
            contact.name = co.contact_person || co.contact_name || co.company_name || co.name || "—";
            contact.phone = co.phone || co.mobile || co.contact_number || "—";
          } catch (_) {}
        }
        if (!cancelled) setSiteContact(contact);

        let buyerTotal = parseFloat(insp.inspection_fee) || 0;
        let platformCommission = 0;
        try {
          const quotRes = await CommonService.get(API_URLS.QA.QUOTATIONS_BY_REQUEST(insp.qa_request_id));
          const list = quotRes?.data && Array.isArray(quotRes.data) ? quotRes.data : [];
          const quotation = list.find((q) => q.quotation_id === insp.quotation_id);
          if (quotation?.additional_notes) {
            const breakdown = parseQAFeeBreakdown(quotation.additional_notes);
            if (breakdown?.totalWithFee != null && breakdown.totalWithFee > 0) {
              buyerTotal = breakdown.totalWithFee;
              platformCommission = breakdown.platformEscrowFee ?? Math.round(buyerTotal * QA_FEE_PERCENT * 100) / 100;
            }
          }
        } catch (_) {}
        const youReceive = Math.max(0, buyerTotal - platformCommission);
        if (!cancelled) {
          setFinancial({
            buyerTotalPaid: buyerTotal,
            platformCommission,
            youReceive,
            currency: insp.currency || "USD",
          });
        }

        const scope = qr?.scope_logistics || {};
        const types = [];
        if (Array.isArray(qr?.inspection_checklist) && qr.inspection_checklist.length > 0) {
          qr.inspection_checklist.forEach((t) => types.push(typeof t === "string" ? t : t?.name || t?.label || String(t)));
        }
        if (scope.inspection_type && !types.length) types.push(scope.inspection_type);
        if (!types.length) types.push("Quality Inspection");
        if (!cancelled) setInspectionTypes(types);
      } catch (err) {
        if (!cancelled) toast.error("Failed to load assignment details");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetchData();
    return () => { cancelled = true; };
  }, [inspectionId, loadInspection]);

  useEffect(() => {
    const refresh = () => {
      loadInspection().catch(() => {});
    };
    window.addEventListener("focus", refresh);
    return () => window.removeEventListener("focus", refresh);
  }, [loadInspection]);

  const formatDate = (dateVal) => {
    if (!dateVal) return "—";
    const d = dateVal instanceof Date ? dateVal : new Date(dateVal);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  const formatSchedule = () => {
    if (!qaRequest) return "—";
    const start = qaRequest.preferred_start_date ? formatDate(qaRequest.preferred_start_date) : null;
    const end = qaRequest.preferred_end_date ? formatDate(qaRequest.preferred_end_date) : null;
    const time = qaRequest.preferred_time || inspection?.inspection_time || "";
    if (start && end) return `${start} – ${end}${time ? `, ${time}` : ""}`;
    if (start) return `${start}${time ? `, ${time}` : ""}`;
    return "—";
  };

  const formatAddress = () => {
    const parts = [
      qaRequest?.inspection_location_address,
      [qaRequest?.inspection_location_city, qaRequest?.inspection_location_state].filter(Boolean).join(", "),
      qaRequest?.inspection_location_country,
    ].filter(Boolean);
    return parts.length ? parts.join(", ") : "—";
  };

  const formatCurrency = (amount) => {
    const num = parseFloat(amount);
    if (isNaN(num)) return "$0.00";
    const currency = financial.currency || "USD";
    if (currency === "USD") {
      return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2 }).format(num);
    }
    return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(num);
  };

  const jobId = inspection ? `WO-${inspection.inspection_id}-${new Date().getFullYear()}` : "—";
  const productName = qaRequest?.scope_logistics?.product_name || qaRequest?.scope_logistics?.product_description || "QA Inspection Service";

  const isP2pLinked = p2pAssignment?.is_p2p_linked === true;
  const jobAccepted = Boolean(p2pAssignment?.job_accepted_at ?? inspection?.job_accepted_at);
  const approvalPending = p2pAssignment?.payment_approval_pending === true;
  const buyerPaid =
    p2pAssignment?.buyer_payment_status === "paid" ||
    String(inspection?.buyer_payment_status || "").trim() === "paid";
  const paymentApproved =
    !p2pAssignment?.payment_approval_status ||
    p2pAssignment.payment_approval_status === "not_required" ||
    p2pAssignment.payment_approval_status === "approved";
  const canStartInspection =
    p2pAssignment?.can_start_inspection === true ||
    (isP2pLinked &&
      jobAccepted &&
      buyerPaid &&
      paymentApproved &&
      !approvalPending &&
      String(inspection?.status || "").toLowerCase() === "scheduled");

  const showAcceptButton = !jobAccepted && String(inspection?.status || "").toLowerCase() === "scheduled";

  const handleStartInspection = async () => {
    if (!canStartInspection || !inspection?.inspection_id) return;
    setStarting(true);
    try {
      const response = await CommonService.post(
        API_URLS.QA.INSPECTION_START(inspection.inspection_id),
        {},
      );
      const ok = response && (response.success === true || response.data !== undefined);
      if (ok) {
        toast.success("Inspection started");
        navigate(buildInspectionReportLocation(inspection.inspection_id, qaRequest));
      } else {
        throw new Error(response?.message || "Failed to start inspection");
      }
    } catch (error) {
      toast.error(error?.response?.data?.error || error?.message || "Failed to start inspection");
    } finally {
      setStarting(false);
    }
  };

  const handleAcceptJob = async () => {
    setAccepting(true);
    try {
      const response = await CommonService.post(
        API_URLS.QA.INSPECTION_ACCEPT_ASSIGNMENT(inspectionId),
        {},
      );
      const payload = response?.data ?? response;
      const mode = payload?.mode;
      const assignmentUx = payload?.p2p_assignment ?? null;

      if (mode === "started") {
        toast.success("Job accepted. Inspection started.");
        navigate(buildInspectionReportLocation(inspection.inspection_id, qaRequest));
        return;
      }

      if (mode === "accepted" || mode === "already_accepted") {
        const ux = assignmentUx ?? {};
        toast.success(
          ux.can_start_inspection
            ? "Job accepted. Buyer payment is complete — you can start the inspection."
            : ux.payment_approval_pending
              ? "Job accepted. Buyer payment approval is in progress."
              : ux.buyer_payment_status === "paid"
                ? "Job accepted. Buyer payment received — you can start the inspection."
                : "Job accepted. Waiting for buyer payment before you can start the inspection.",
        );
        await loadInspection();
        if (assignmentUx) setP2pAssignment(assignmentUx);
        if (ux.can_start_inspection || ux.buyer_payment_status === "paid") {
          return;
        }
        navigate("/auction/qa/dashboard/inspections");
        return;
      }

      throw new Error(response?.message || "Failed to accept job");
    } catch (error) {
      toast.error(error?.response?.data?.error || error?.message || "Failed to accept job");
    } finally {
      setAccepting(false);
    }
  };

  const handleDecline = () => {
    setDeclining(true);
    toast.info("Assignment declined.");
    navigate("/auction/qa/dashboard/inspections");
  };

  if (loading) {
    return (
      <div className="qa-assignment-page">
        <div className="qa-assignment-card">
          <p className="text-muted">Loading assignment…</p>
        </div>
      </div>
    );
  }

  if (!inspection) {
    return (
      <div className="qa-assignment-page">
        <div className="qa-assignment-card">
          <p className="text-danger">Inspection not found.</p>
          <button type="button" className="btn btn-outline-secondary" onClick={() => navigate("/auction/qa/dashboard/inspections")}>
            Back to Inspections
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="qa-assignment-page">
      <div className="qa-assignment-card">
        <h1 className="qa-assignment-title">NEW ASSIGNMENT</h1>
        <p className="qa-assignment-subtitle">JOB ALLOCATION AND ACCEPTANCE</p>

        {jobAccepted && isP2pLinked ? (
          <div className="qa-assignment-p2p-notice" role="status">
            {canStartInspection ? (
              <p>
                Buyer payment is complete. Click <strong>Start Inspection</strong> below to begin the
                on-site inspection.
              </p>
            ) : approvalPending ? (
              <p>
                Assignment accepted. Internal <strong>payment approval</strong> is in progress for the buyer.
                Start Inspection will appear after approval and buyer payment are complete.
              </p>
            ) : buyerPaid ? (
              <p>
                Buyer payment has been received. Return to <strong>My Inspections</strong> or use{" "}
                <strong>Start Inspection</strong> when it becomes available.
              </p>
            ) : (
              <p>
                Assignment accepted. The buyer will be notified to pay the inspection fee.
                Start Inspection will appear on My Inspections once payment is complete.
              </p>
            )}
          </div>
        ) : null}

        <div className="qa-assignment-grid">
          <div className="qa-assignment-summary">
            <h2 className="section-label">ASSIGNMENT SUMMARY</h2>
            <p className="job-id">Job ID: {jobId}</p>

            <div className="summary-block">
              <span className="block-label">PRODUCT</span>
              <p className="block-value product-name">{productName}</p>
            </div>

            <div className="summary-block">
              <span className="block-label">INSPECTION TYPES REQD</span>
              <div className="inspection-tags">
                {inspectionTypes.map((t, i) => (
                  <span key={i} className={`inspection-tag ${i === 0 ? "tag-purple" : "tag-green"}`}>
                    {t}
                  </span>
                ))}
              </div>
            </div>

            <div className="summary-block">
              <span className="block-label">SITE CONTACT</span>
              <p className="block-value">{siteContact.name} {siteContact.phone !== "—" ? `(${siteContact.phone})` : ""}</p>
            </div>

            <div className="summary-block">
              <span className="block-label">SCHEDULE</span>
              <p className="block-value">{formatSchedule()}</p>
            </div>

            <div className="summary-block">
              <span className="block-label">SITE ADDRESS</span>
              <p className="block-value">{formatAddress()}</p>
            </div>
          </div>

          <div className="qa-assignment-actions">
            <div className="financial-breakdown">
              <h2 className="financial-label">FINANCIAL BREAKDOWN</h2>
              <div className="financial-row">
                <span>{isP2pLinked ? "BUYER TOTAL (ESCROW)" : "BUYER TOTAL PAID"}</span>
                <span>{formatCurrency(financial.buyerTotalPaid)}</span>
              </div>
              <div className="financial-row deduction">
                <span>PLATFORM COMMISSION (25%)</span>
                <span>-{formatCurrency(financial.platformCommission)}</span>
              </div>
              <hr className="financial-divider" />
              <div className="financial-row you-receive">
                <span>YOU RECEIVE</span>
                <span>{formatCurrency(financial.youReceive)}</span>
              </div>
            </div>

            {showAcceptButton ? (
              <button
                type="button"
                className="btn-accept-job"
                disabled={accepting}
                onClick={handleAcceptJob}
              >
                {accepting ? "Accepting…" : "ACCEPT JOB"}
              </button>
            ) : null}

            {canStartInspection ? (
              <button
                type="button"
                className="btn-accept-job"
                disabled={starting}
                onClick={handleStartInspection}
              >
                {starting ? "Starting…" : "START INSPECTION"}
              </button>
            ) : null}

            {jobAccepted && !canStartInspection ? (
              <button
                type="button"
                className="btn-accept-job"
                onClick={() => navigate("/auction/qa/dashboard/inspections")}
              >
                GO TO MY INSPECTIONS
              </button>
            ) : null}

            <button
              type="button"
              className="btn-decline"
              disabled={declining}
              onClick={handleDecline}
            >
              DECLINE ASSIGNMENT
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
